import React, { useEffect, useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import GenericButton from '../../../Components/GenericButton/GenericButton';
import TextInput from '../../../Components/GenericInputText/TextInput';
import { axiosInstance } from '../../../api/axios';
import { HttpSuccessCodes } from '../../errorhandling/HttpErrorCodes';
import HttpErrorHandler from '../../errorhandling/HttpErrorHandler';
import APIEndpoints from '../../api/APIEndpoints';
import Skeleton from 'src/Member/components/footer/Skeleton';
import { PageNotFoundSVG } from 'src/Member/img/ServerErrorSVG';

const SecurityQuestions = ({
  email,
  userDetails,
  currentStep,
  nextStep,
  prevStep,
  accountLockedMessage,
}) => {
  const cacheBuster = (url) => `${url}?cb=${Date.now()}`;

  const [questions, setQuestions] = useState([]);
  const [serverError, setServerError] = useState(false);
  const [validateSecurityQuestionsError, setValidateSecurityQuestionsError] =
    useState(false);
  const [
    validateSecurityQuestionsErrorMsg,
    setValidateSecurityQuestionsErrorMsg,
  ] = useState('');
  const [showSkeletonLoader, setShowSkeletonLoader] = useState(true);
  const [displayErrorMsg, setDisplayErrorMsg] = useState(null);

  const securityQuestionschema = yup.object({
    answer1: yup
      .string()
      .required('Answer is required')
      .min(4, 'Answer must be at least 4 characters')
      .max(128, 'Answer must not exceed 128 characters')
      .test('answer_1_2_same', 'Answer should not be same', function (value) {
        return value ? value !== this.parent.answer2 : true;
      })
      .test('answer_1_3_same', 'Answer should not be same', function (value) {
        return value ? value !== this.parent.answer3 : true;
      }),
    answer2: yup
      .string()
      .required('Answer is required')
      .min(4, 'Answer must be at least 4 characters')
      .max(128, 'Answer must not exceed 128 characters')
      .test('answer_2_1_same', 'Answer should not be same', function (value) {
        return value ? value !== this.parent.answer1 : true;
      })
      .test('answer_2_3_same', 'Answer should not be same', function (value) {
        return value ? value !== this.parent.answer3 : true;
      }),
    answer3: yup
      .string()
      .required('Answer is required')
      .min(4, 'Answer must be at least 4 characters')
      .max(128, 'Answer must not exceed 128 characters')
      .test('answer_3_1_same', 'Answer should not be same', function (value) {
        return value ? value !== this.parent.answer1 : true;
      })
      .test('answer_3_2_same', 'Answer should not be same', function (value) {
        return value ? value !== this.parent.answer2 : true;
      }),
  });

  const getUserSecuirtyQuestions = async () => {
    setShowSkeletonLoader(true);
    try {
      const UserSecuirtyQuestionsResponse = await axiosInstance.get(
        cacheBuster(
          APIEndpoints.getSecurityQuestionsByUserID() + userDetails.Oid,
        ),
      );

      if (
        UserSecuirtyQuestionsResponse.status ===
        HttpSuccessCodes.HTTP_SUCCESS_CODE_200
      ) {
        if (UserSecuirtyQuestionsResponse?.data?.Response?.SecurityQuestions) {
          console.log(
            'UserSecuirtyQuestionsResponse?.data?.Response?',
            UserSecuirtyQuestionsResponse?.data?.Response,
          );
          setQuestions(
            UserSecuirtyQuestionsResponse?.data?.Response.SecurityQuestions,
          );
        }
      }
      setShowSkeletonLoader(false);
    } catch (error) {
      setShowSkeletonLoader(false);
      console.log(error);
      setServerError(true);
      setDisplayErrorMsg(error?.response?.data?.message);
    }
  };

  const validateSecurityQuestions = async (payload) => {
    try {
      const response = await axiosInstance.post(
        APIEndpoints.validateSecurityQuestions(),
        payload,
      );

      if (
        response.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_200 ||
        response.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_201
      ) {
        const responseData = response.data;
        console.log('responseData', responseData);
        nextStep();
      } else {
        HttpErrorHandler.DisplayServerErrorMessage(response.status, '');
      }
    } catch (error) {
      console.log(error.response);
      setValidateSecurityQuestionsError(true);
      setValidateSecurityQuestionsErrorMsg(error.response?.data?.message);
    }
  };

  useEffect(() => {
    getUserSecuirtyQuestions();
  }, []);
  if (serverError) {
    //return <Navigate to="/admin/server-error" push={true}></Navigate>;
    prevStep();
    accountLockedMessage(displayErrorMsg);
  }

  return currentStep === 2 && questions && questions.length !== 0 ? (
    <Col
      md={8}
      className="security-questions-container security-questions-container-box"
    >
      <Row>
        <Col md={12}>
          <p>
            You are resetting password for <strong>{email}</strong>
          </p>
        </Col>
      </Row>

      {!showSkeletonLoader ? (
        <>
          <Row className="mt-4 mb-4">
            <Col md={12} className="heading-4">
              Please answer the following security questions.
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Formik
                validationSchema={securityQuestionschema}
                initialValues={{
                  answer1: '',
                  answer2: '',
                  answer3: '',
                }}
                onSubmit={(values, actions) => {
                  console.log('VALUES', values);
                  setValidateSecurityQuestionsError(false);
                  const SecurityQuestions = [];
                  questions &&
                    questions.map((question, index) =>
                      SecurityQuestions.push({
                        ...question,
                        answer: values[`answer${index + 1}`],
                      }),
                    );
                  const payloadObject = {
                    SecurityQuestions: SecurityQuestions,
                    UserID: email,
                  };
                  validateSecurityQuestions(payloadObject);
                }}
              >
                {({
                  values,
                  touched,
                  errors,
                  status,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                }) => {
                  return (
                    <Form onSubmit={handleSubmit}>
                      {questions &&
                        questions.map((question, index) => (
                          <Form.Group className="mb24" key={question.oid}>
                            <Form.Label>
                              <div>{`Security Question ${index + 1}`}</div>
                              <div className="question">
                                {question.securityquestion}
                              </div>
                            </Form.Label>
                            <TextInput
                              isRequired={true}
                              label="Enter Answer"
                              className="mb-0 mt-4"
                              inputProps={{
                                type: 'text',
                                spellCheck: 'off',
                                autoCorrect: 'off',
                                autoComplete: 'off',
                                autoFocus: false,
                                onBlur: (e) => {
                                  handleBlur(e);
                                  const answerVal =
                                    e.currentTarget.value.trim();
                                  setFieldValue(
                                    `answer${index + 1}`,
                                    answerVal,
                                  );
                                },
                                onChange: (e) => {
                                  handleChange(e);
                                },
                                name: `answer${index + 1}`,
                                id: `answer${index + 1}`,
                                value: values[`answer${index + 1}`],
                                placeholder: '',
                                isInvalid:
                                  touched[`answer${index + 1}`] &&
                                  errors[`answer${index + 1}`],
                              }}
                              feedBackMessage={
                                touched[`answer${index + 1}`] &&
                                errors[`answer${index + 1}`]
                              }
                              ariaInvalid={
                                errors[`answer${index + 1}`] ? true : null
                              }
                              ariaDescribedby={`err_answer${index + 1}`}
                            />
                          </Form.Group>
                        ))}
                      {validateSecurityQuestionsError && (
                        <Row className="security-questions m-2">
                          <div className="d-flex">
                            <i className="fa fa-exclamation-circle font-18 mr-2" />
                            {validateSecurityQuestionsErrorMsg}
                          </div>
                        </Row>
                      )}
                      <Row>
                        <Col>
                          <GenericButton
                            type="submit"
                            variant="primary"
                            className="float-right"
                          >
                            Continue
                          </GenericButton>
                        </Col>
                      </Row>
                    </Form>
                  );
                }}
              </Formik>
            </Col>
          </Row>
        </>
      ) : (
        <Skeleton count={10} />
      )}
    </Col>
  ) : !showSkeletonLoader ? (
    <div className="questions-not-found">
      <Row>
        <Col className="text-center questions-not-found-container">
          <div>
            <PageNotFoundSVG />
            <div className="content-box">
              <div role="alert">Security questions has not been set up.</div>
              <p role="alert">
                Contact your Administrator to reset your password.
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  ) : (
    <Skeleton count={10} />
  );
};

export default SecurityQuestions;
